/** One tab as the bar measured it, in bar order. */
export type MeasuredTab = { key: string; width: number; collapsible: boolean };

/** How the bar lays its tabs out. */
export type TabFit = {
	/** The tabs that moved into the "More" menu, in bar order. */
	collapsed: readonly string[];
	/** Even with every collapsible tab in "More" the bar is wider than its room — it scrolls rather than clip. */
	overflows: boolean;
};

export const ALL_TABS_INLINE: TabFit = { collapsed: [], overflows: false };

type FitInput = {
	tabs: readonly MeasuredTab[];
	/** The "More" tab's own width — it joins the bar as soon as one tab moves into it. */
	moreWidth: number;
	/** The bar's width. */
	available: number;
};

const totalWidth = (tabs: readonly MeasuredTab[]): number => tabs.reduce((total, tab) => total + tab.width, 0);

/**
 * Which tabs leave the bar for its "More" menu. None while every tab fits. Otherwise collapsible tabs leave from the END
 * of the bar, one at a time, until the tabs left and the "More" tab fit; a tab that isn't collapsible never leaves. When
 * those alone outgrow the bar, `overflows` says so.
 */
export const fitTabsToWidth = ({ tabs, moreWidth, available }: FitInput): TabFit => {
	if (totalWidth(tabs) <= available) return ALL_TABS_INLINE;
	const inline = [...tabs];
	const collapsed: string[] = [];
	const fits = () => totalWidth(inline) + moreWidth <= available;
	for (let index = inline.length - 1; index >= 0 && !fits(); index -= 1) {
		if (!inline[index].collapsible) continue;
		collapsed.unshift(inline[index].key);
		inline.splice(index, 1);
	}
	// Nothing could leave: no "More" tab joins, and the tabs as they are don't fit (the first check above).
	return { collapsed, overflows: collapsed.length === 0 || !fits() };
};

export const isSameTabFit = (current: TabFit, next: TabFit): boolean =>
	current.overflows === next.overflows &&
	current.collapsed.length === next.collapsed.length &&
	current.collapsed.every((key, index) => next.collapsed[index] === key);
